import {
    type ReactNode,
    useContext,
    type FC,
    useId
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ITimelineItemProps from "./type";
import type {
    TimelineItemWebPropsType
} from "./type";
import stylesheet, {
    getTimelineSurfaceRadius,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    TimelinePartContentProps
} from "../../type";
import {
    TimelinePositionContext,
    withTimelineContext
} from "../../context";
import {
    getTimelineNodeText,
    getTimelineDate
} from "../../util";
import {
    getTimelineMeasures
} from "../../stylesheet";
import TimelineMarker from "../../internal/marker";
import TimelineTime from "../../internal/time";
import Attachment from "../../../attachment";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const TIMELINE_EMPTY_ATTACHMENTS: NonNullable<ITimelineItemProps["attachments"]> = [];

const TimelineItemContent: FC<TimelinePartContentProps<ITimelineItemProps>> = ({
    attachments = TIMELINE_EMPTY_ATTACHMENTS,
    customTheme: customThemeProp,
    "aria-label": ariaLabel,
    accessibilityLabel,
    type = "neutral",
    isDot = false,
    description,
    children,
    context,
    onPress,
    avatar,
    style,
    title,
    icon,
    time,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlPaddings,
        shapeConfigs,
        touchTargets,
        avatarSizes,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const position = useContext(TimelinePositionContext);

    const pointerType = usePointerType();

    const titleId = useId();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
    });

    const isPressable = !!onPress;

    const isFeedItem = context.isFeed && !isPressable;

    const isSurfaced = isPressable || context.isFeed;

    const isLast = position.isLast && !(position.isFinal && context.hasMore);

    const isDashed = position.isLast && position.isFinal && context.hasMore;

    const date = getTimelineDate({
        value: time
    });

    const measures = getTimelineMeasures({
        size: context.size,
        shapeConfigs,
        avatarSizes,
        typography,
        iconSizes,
        spaces
    });

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle,
        wrapper: wrapperDynamicStyle,
        head: headDynamicStyle,
        line: lineDynamicStyle,
        rail: railDynamicStyle
    } = useStyles({
        isFocusVisible: isSurfaced && isFocusVisible,
        controlPaddings,
        shapeConfigs,
        touchTargets,
        pointerType,
        isSurfaced,
        isDashed,
        measures,
        radiuses,
        borders,
        colors,
        isLast,
        spaces
    });

    const webProps: TimelineItemWebPropsType = Platform.OS === "web" && context.isFeed ? {
        "aria-posinset": position.feedIndex + 1,
        "aria-setsize": position.feedCount,
        "aria-labelledby": titleId,
        tabIndex: 0
    } : {};

    const renderTime = () => {
        if(!date) {
            return null;
        }

        return <View
            style={stylesheet.time}
        >
            <TimelineTime
                isTooltipVisible={isFeedItem && isFocusVisible}
                customTheme={customTheme}
                date={date}
            />
        </View>;
    };

    const renderRow = (): ReactNode => {
        return <View
            style={stylesheet.row}
        >
            <View
                style={[
                    stylesheet.rail,
                    railDynamicStyle
                ]}
            >
                <TimelineMarker
                    customTheme={customTheme}
                    measures={measures}
                    avatar={avatar}
                    isDot={isDot}
                    type={type}
                    icon={icon}
                />
            </View>
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                <View
                    style={[
                        stylesheet.head,
                        headDynamicStyle
                    ]}
                >
                    <Text
                        customTheme={customTheme}
                        style={stylesheet.title}
                        variant="bodyMediumSize"
                        id={titleId}
                        color="mid"
                    >
                        {title}
                    </Text>
                    {renderTime()}
                </View>
                {
                    description ?
                        <Text
                            variant={context.size === "small" ? "bodySmallSize" : "bodyMediumSize"}
                            style={stylesheet.description}
                            customTheme={customTheme}
                            color="mid"
                        >
                            {description}
                        </Text>
                    :
                        null
                }
                {
                    attachments.length ?
                        <View
                            style={stylesheet.attachments}
                        >
                            {attachments.map((attachment, index) => <Attachment
                                key={`${attachment.title}-${index}`}
                                customTheme={customTheme}
                                size="small"
                                {...attachment}
                            />)}
                        </View>
                    :
                        null
                }
                {children}
            </View>
        </View>;
    };

    const renderSurface = () => {
        return <View
            style={surfaceDynamicStyle}
        >
            {
                isPressable ?
                    <StateLayer
                        borderRadius={getTimelineSurfaceRadius({
                            radiuses
                        })}
                        customTheme={customTheme}
                        overlayColor="default"
                        isHovered={isHovered}
                        isPressed={isPressed}
                    />
                :
                    null
            }
            {renderRow()}
        </View>;
    };

    const renderBody = () => {
        if(isPressable) {
            return <Pressable
                {...interactionHandlers}
                aria-label={ariaLabel ?? accessibilityLabel ?? getTimelineNodeText({
                    node: title
                })}
                style={[
                    stylesheet.wrapper,
                    wrapperDynamicStyle
                ]}
                onPress={onPress}
                role="button"
            >
                {renderSurface()}
            </Pressable>;
        }

        if(isFeedItem) {
            return <View
                style={[
                    stylesheet.wrapper,
                    wrapperDynamicStyle
                ]}
            >
                {renderSurface()}
            </View>;
        }

        return renderRow();
    };

    const isShowLine = !isLast;

    return <View
        {...props}
        {...webProps}
        aria-label={isFeedItem ? ariaLabel ?? accessibilityLabel : undefined}
        onFocus={isFeedItem ? interactionHandlers.onFocus : props.onFocus}
        onBlur={isFeedItem ? interactionHandlers.onBlur : props.onBlur}
        role={context.isFeed ? "article" : "listitem"}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {
            isShowLine ?
                <View
                    style={[
                        stylesheet.line,
                        lineDynamicStyle
                    ]}
                    aria-hidden={true}
                />
            :
                null
        }
        {renderBody()}
    </View>;
};
export default withTimelineContext("Item", TimelineItemContent);
